import { HttpClient, RequestParams } from "@/utils/http-client";
import { PlanType } from "./Plans";
import { CourseType } from "./Courses";

export interface ProductListParamsType {
  /** title substring search */
  title?: string;
  /** ProductType enum */
  type?: string;
  course_id?: number;
  owner_id?: number;
  parent_id?: number;
  limit?: number;
  offset?: number;
}

export enum ProductCurrencyType {
  Usd = "usd",
  Eur = "eur",
  Rub = "rub",
}

export enum ProductTypeType {
  Course = "course",
  Marathon = "marathon",
  Lesson = "lesson",
  Astrology = "astrology",
  AstrologyBundle = "astrology_bundle",
  MarathonBundle = "marathon_bundle",
  Subscription = "subscription",
  Bundle = "bundle",
  Resource = "resource",
  EventTicket = "event_ticket",
  EventDoubleTicket = "event_double_ticket",
  EventBooking = "event_booking",
  EventDoubleBooking = "event_double_booking",
  Webinar = "webinar",
  Media = "media",
}

export enum ProductStatusType {
  Active = "active",
  Incomplete = "incomplete",
  Unavailable = "unavailable",
}

export interface CategoryType {
  /** @format int64 */
  id?: number;
  image?: string;
  /** Category is a type of certificate */
  parent_category?: CategoryType;
  /**
   * Optional
   * @format int64
   */
  parent_id?: number;
  products?: ProductType[];
  title?: string;
}
export interface MarkerType {
  /**
   * Time in seconds from start
   * @format int64
   */
  time?: number;
  title?: string;
}

export interface ResourceParamsType {
  format?: string;
}

export enum ResourceFormatType {
  Pdf = "pdf",
  Document = "document",
  Video = "video",
  Audio = "audio",
  Image = "image",
}

export interface ResourceType {
  author_name?: string;
  /** @format date-time */
  created_at?: string;
  description?: string;
  html?: string;
  /** @format int64 */
  id?: number;
  image_url?: string;
  /** Markers apply for audio and video resource types */
  markers?: MarkerType[];
  /** Model for ResourceParams */
  params?: ResourceParamsType;
  stream_id?: string;
  tags?: string[];
  title?: string;
  format?: ResourceFormatType;
  url_absolute?: string;
  url_relative?: string;
}

export interface DisplayMediaBlockType {
  /** Extra images if needed */
  extra_images_urls?: string[];
  /** Absolute url of primary image */
  primary_image_url?: string;
  /** Absolute url of secondary image */
  secondary_image_url?: string;
  /** Absolute url of video */
  video_url?: string;
}

export interface ProductType {
  /** @format date-time */
  activation_date?: string;
  author?: string;
  categories?: CategoryType[];
  /** Course model */
  course?: CourseType;
  /**
   * Not empty if product is course
   * @format int64
   */
  course_id?: number;
  /** @format date-time */
  created_at?: string;
  /**
   * just json for few display cases like calculated total price or upgrade price
   * eur CurrencyEUR
   * rub CurrencyRUB
   */
  currency?: ProductCurrencyType;
  /** @format date-time */
  deleted_at?: string;
  description?: string;
  /**
   * How many days product is valid from purchase date
   * @format int64
   */
  duration_days?: number;
  /** @format float */
  exam_price_eur?: number;
  /** @format float */
  exam_price_rub?: number;
  free?: boolean;
  /**
   * Price per group lesson. in same currency as price. If not defined - default one is taken
   * @format float
   */
  group_lesson_price_eur?: number;
  /** @format float */
  group_lesson_price_rub?: number;
  html?: string;
  /** @format int64 */
  id?: number;
  images?: DisplayMediaBlockType;
  /** @format int64 */
  lesson_id?: number;
  /**
   * Optional
   * @format int64
   */
  parent_id?: number;
  /** Product is a model for product - anything you can buy */
  parent_product?: ProductType;
  plans?: PlanType[];
  /** @format float */
  price_eur?: number;
  /** @format float */
  price_pro_eur?: number;
  /**
   * Calculated field for courses, pro price including group lessons price and exam price
   * @format float
   */
  price_pro_rub?: number;
  /** @format float */
  price_rub?: number;
  /** Child Products, connected to this bundle - they have parent_id equal to current product. Read-only */
  products_bundle?: ProductType[];
  /** Model for Resource */
  resource?: ResourceType;
  /** @format int64 */
  resource_id?: number;
  status?: ProductStatusType;
  subtitle?: string;
  title?: string;
  type?: ProductTypeType;
  /**
   * Price for update from basic to pro in selected currency
   * @format float
   */
  upgrade_to_pro_price_eur?: number;
  /** @format float */
  upgrade_to_pro_price_rub?: number;
  /**
   * Not empty if product is webinar
   * @format int64
   */
  webinar_id?: number;
}

export class Products<
  SecurityDataType = unknown
> extends HttpClient<SecurityDataType> {
  /**
   * @description Returns products list by filter, with limit/offset pagination, and total count in header
   *
   * @tags products
   * @name ProductList
   * @summary Get list of products by filter
   * @request GET:/products
   * @response `200` `(ProductType)[]` Product object
   * @response `500` `void` error
   */
  productList = (query: ProductListParamsType, params: RequestParams = {}) =>
    this.request<ProductType[], void>({
      path: `/products`,
      method: "GET",
      query: query,
      format: "json",
      ...params,
    });
}
